import * as THREE from 'three';
import { hexToWorld, worldToHex } from '@somnhold/sim';
import { WORLD_PER_HEX } from './coords.ts';

/**
 * Sheets of ice over a set of the board's cells (the Long Hunt's floes and the White Island: docs/sendas/barbaro.md,
 * chapters 3 and 4): one mesh per sheet with a ragged edge, never the grid's hexes. Each cell is a fan from its middle to
 * its six corners; a corner's place is jittered by a hash of where it is, so neighbours share it and no seam opens. On
 * the edge the corners and the edges' middles are pushed out and in further, so the outline wanders; under the edge a
 * skirt of pale blue ice goes down into the water, a little wider at its foot. The top takes a frost texture laid by the
 * world's x and z (one canvas, built once and shared), so no tile repeats on a cell.
 *
 * Cost: one geometry per sheet (a few hundred triangles a floe), one material per layer, one texture for all.
 */

const K = WORLD_PER_HEX / 1000;
/**
 * A hex's corner radius, sim units (pointy-top, 1000 across the flats). Exact, and every corner snapped to whole units:
 * a rounded radius left neighbours' corners apart by a hair, and the sea showed through the seams as a grid of lines.
 */
const CR = 1000 / Math.sqrt(3);

const hash = (x: number, y: number, seed: number): number => {
  let h = (Math.imul(x | 0, 374761393) ^ Math.imul(y | 0, 668265263) ^ Math.imul(seed | 0, 2246822519)) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
};

export interface IceSheetOpts {
  seed: number;
  /** World y of the top and of the skirt's foot (under the water). */
  top: number;
  foot: number;
  /** How far the edge wanders (sim units). */
  rag?: number;
  /** The top's two colours (noise between them) and the skirt's. */
  topA?: string;
  topB?: string;
  side?: string;
  /** World units a frost tile covers. */
  tile?: number;
}

/** The sheet over these cells (axial q, r). */
export const iceSheetGeometry = (cells: readonly { q: number; r: number }[], o: IceSheetOpts): THREE.BufferGeometry => {
  const member = new Set(cells.map((c) => `${c.q},${c.r}`));
  const rag = o.rag ?? 230;
  const tile = o.tile ?? 7;
  const cA = new THREE.Color(o.topA ?? '#e6eef4');
  const cB = new THREE.Color(o.topB ?? '#bcd0de');
  const cS = new THREE.Color(o.side ?? '#93b1c6');
  const cEdge = new THREE.Color('#a9c3d4');
  const pos: number[] = [];
  const col: number[] = [];
  const uv: number[] = [];
  const tmp = new THREE.Color();
  // Whether a world point (sim) is inside the set.
  const inside = (x: number, y: number): boolean => {
    const h = worldToHex(Math.round(x), Math.round(y));
    return member.has(`${h.q},${h.r}`);
  };
  /** A corner or an edge's middle, jittered (more on the outline; its outward push from which side). */
  const jit = (x: number, y: number, edge: boolean, ox: number, oy: number): [number, number] => {
    const kx = Math.round(x / 10);
    const ky = Math.round(y / 10);
    const a = hash(kx, ky, o.seed) * Math.PI * 2;
    const m = (edge ? rag : rag * 0.28) * (0.4 + 0.6 * hash(ky, kx, o.seed + 1));
    let jx = Math.cos(a) * m;
    let jy = Math.sin(a) * m;
    if (edge) {
      // In or out, by the hash: the outline wanders.
      const out = (hash(kx + 7, ky - 3, o.seed + 2) - 0.42) * rag * 1.3;
      jx += ox * out;
      jy += oy * out;
    }
    return [x + jx, y + jy];
  };
  const shade = (x: number, y: number, edgeK: number): THREE.Color => {
    const n = 0.5 + 0.25 * Math.sin(x * 0.0007 + y * 0.0004) * Math.cos(y * 0.0009 - x * 0.0003) + 0.25 * hash(Math.round(x / 300), Math.round(y / 300), o.seed + 5);
    tmp.copy(cA).lerp(cB, Math.max(0, Math.min(1, n)));
    return tmp.lerp(cEdge, edgeK * 0.55);
  };
  const push = (x: number, y: number, h: number, c: THREE.Color): void => {
    pos.push(x * K, h, y * K);
    col.push(c.r, c.g, c.b);
    uv.push((x * K) / tile, (y * K) / tile);
  };
  for (const c of cells) {
    const m = hexToWorld(c.q, c.r);
    const corner: [number, number][] = [];
    const onEdge: boolean[] = [];
    for (let k = 0; k < 6; k++) {
      const a = ((30 + 60 * k) * Math.PI) / 180;
      const x = Math.round(m.x + Math.cos(a) * CR);
      const y = Math.round(m.y + Math.sin(a) * CR);
      // On the outline when any of the three hexes round the corner is out.
      const d = Math.PI / 6;
      const e = !inside(m.x + Math.cos(a - d) * 1000, m.y + Math.sin(a - d) * 1000) || !inside(m.x + Math.cos(a + d) * 1000, m.y + Math.sin(a + d) * 1000);
      corner.push(jit(x, y, e, Math.cos(a), Math.sin(a)));
      onEdge.push(e);
    }
    const mid = jit(m.x, m.y, false, 0, 0);
    for (let k = 0; k < 6; k++) {
      const a0 = corner[k] as [number, number];
      const a1 = corner[(k + 1) % 6] as [number, number];
      // The edge's neighbour: across the edge's middle.
      const ex = m.x + Math.cos(((60 + 60 * k) * Math.PI) / 180) * 866;
      const ey = m.y + Math.sin(((60 + 60 * k) * Math.PI) / 180) * 866;
      const open = !inside(ex, ey);
      const eo = Math.cos(((60 + 60 * k) * Math.PI) / 180);
      const en = Math.sin(((60 + 60 * k) * Math.PI) / 180);
      const raw = [Math.round(m.x + (Math.cos(((30 + 60 * k) * Math.PI) / 180) + Math.cos(((90 + 60 * k) * Math.PI) / 180)) * CR * 0.5), Math.round(m.y + (Math.sin(((30 + 60 * k) * Math.PI) / 180) + Math.sin(((90 + 60 * k) * Math.PI) / 180)) * CR * 0.5)];
      const em = jit(raw[0] as number, raw[1] as number, open, eo, en);
      const ek0 = onEdge[k] ? 1 : 0;
      const ek1 = onEdge[(k + 1) % 6] ? 1 : 0;
      // The top: two triangles a wedge (centre, corner, middle; centre, middle, next corner).
      const cm = shade(mid[0], mid[1], 0).clone();
      const c0 = shade(a0[0], a0[1], ek0).clone();
      const c1 = shade(a1[0], a1[1], ek1).clone();
      const ce = shade(em[0], em[1], open ? 1 : 0).clone();
      push(mid[0], mid[1], o.top, cm);
      push(em[0], em[1], o.top, ce);
      push(a0[0], a0[1], o.top, c0);
      push(mid[0], mid[1], o.top, cm);
      push(a1[0], a1[1], o.top, c1);
      push(em[0], em[1], o.top, ce);
      if (!open) continue;
      // The skirt under an open edge: down to the foot, flaring out a little.
      for (const [p, q] of [
        [a0, em],
        [em, a1],
      ] as const) {
        const fl = 140;
        const pf: [number, number] = [p[0] + eo * fl, p[1] + en * fl];
        const qf: [number, number] = [q[0] + eo * fl, q[1] + en * fl];
        const top = cS.clone().lerp(cEdge, 0.5);
        push(p[0], p[1], o.top, top);
        push(pf[0], pf[1], o.foot, cS);
        push(q[0], q[1], o.top, top);
        push(q[0], q[1], o.top, top);
        push(pf[0], pf[1], o.foot, cS);
        push(qf[0], qf[1], o.foot, cS);
      }
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.computeVertexNormals();
  return g;
};

let frost: THREE.CanvasTexture | null = null;
let frostUsers = 0;

/**
 * The frost on the ice: pale streaks, hair cracks and a few deeper blue ones, frost flowers; tiling (drawn with wrap).
 * One for every sheet; `releaseIceTexture` when a layer goes.
 */
export const iceTexture = (): THREE.CanvasTexture => {
  frostUsers++;
  if (frost) return frost;
  const N = 512;
  const cv = document.createElement('canvas');
  cv.width = cv.height = N;
  const g = cv.getContext('2d') as CanvasRenderingContext2D;
  g.fillStyle = '#ffffff';
  g.fillRect(0, 0, N, N);
  let sd = 9137;
  const R = (): number => ((sd = (Math.imul(sd, 1103515245) + 12345) >>> 0) / 4294967296);
  const wrap = (fn: (ox: number, oy: number) => void): void => {
    for (const ox of [-N, 0, N]) for (const oy of [-N, 0, N]) fn(ox, oy);
  };
  // Soft mottling.
  for (let i = 0; i < 70; i++) {
    const x = R() * N;
    const y = R() * N;
    const r = 30 + R() * 90;
    const a = 0.05 + R() * 0.08;
    wrap((ox, oy) => {
      const gr = g.createRadialGradient(x + ox, y + oy, 0, x + ox, y + oy, r);
      gr.addColorStop(0, `rgba(150,180,205,${a})`);
      gr.addColorStop(1, 'rgba(150,180,205,0)');
      g.fillStyle = gr;
      g.fillRect(x + ox - r, y + oy - r, r * 2, r * 2);
    });
  }
  // Wind streaks of snow (white over the base).
  for (let i = 0; i < 40; i++) {
    const x = R() * N;
    const y = R() * N;
    const l = 40 + R() * 120;
    g.strokeStyle = `rgba(255,255,255,${0.25 + R() * 0.3})`;
    g.lineWidth = 2 + R() * 5;
    wrap((ox, oy) => {
      g.beginPath();
      g.moveTo(x + ox, y + oy);
      g.lineTo(x + ox + l, y + oy + l * 0.18);
      g.stroke();
    });
  }
  // Hair cracks: jagged, branching; a few deeper and bluer.
  const crack = (x0: number, y0: number, a0: number, len: number, w: number, deep: boolean): void => {
    let x = x0;
    let y = y0;
    let a = a0;
    const pts: [number, number][] = [[x, y]];
    for (let s = 0; s < len; s++) {
      a += (R() - 0.5) * 0.9;
      x += Math.cos(a) * 9;
      y += Math.sin(a) * 9;
      pts.push([x, y]);
      if (s > 3 && R() < 0.07 && len > 10) crack(x, y, a + (R() < 0.5 ? 1 : -1) * (0.6 + R() * 0.6), Math.floor(len * 0.5), w * 0.7, false);
    }
    g.strokeStyle = deep ? 'rgba(52,96,132,0.75)' : 'rgba(84,122,150,0.42)';
    g.lineWidth = w;
    wrap((ox, oy) => {
      g.beginPath();
      pts.forEach(([px, py], i) => (i ? g.lineTo(px + ox, py + oy) : g.moveTo(px + ox, py + oy)));
      g.stroke();
    });
  };
  for (let i = 0; i < 16; i++) crack(R() * N, R() * N, R() * Math.PI * 2, 12 + Math.floor(R() * 22), 1 + R() * 1.2, i < 4);
  // Frost flowers.
  for (let i = 0; i < 30; i++) {
    const x = R() * N;
    const y = R() * N;
    g.strokeStyle = 'rgba(255,255,255,0.55)';
    g.lineWidth = 1;
    wrap((ox, oy) => {
      for (let k = 0; k < 6; k++) {
        const a = (k / 6) * Math.PI * 2;
        g.beginPath();
        g.moveTo(x + ox, y + oy);
        g.lineTo(x + ox + Math.cos(a) * 7, y + oy + Math.sin(a) * 7);
        g.stroke();
      }
    });
  }
  frost = new THREE.CanvasTexture(cv);
  frost.wrapS = frost.wrapT = THREE.RepeatWrapping;
  frost.colorSpace = THREE.SRGBColorSpace;
  frost.anisotropy = 4;
  return frost;
};

export const releaseIceTexture = (): void => {
  frostUsers = Math.max(0, frostUsers - 1);
  if (frostUsers === 0 && frost) {
    frost.dispose();
    frost = null;
  }
};

/** The ice's material for a layer (vertex colours over the shared frost). */
export const iceMaterial = (): THREE.MeshStandardMaterial => new THREE.MeshStandardMaterial({ vertexColors: true, map: iceTexture(), roughness: 0.38, metalness: 0.04, side: THREE.DoubleSide });
